/**
 * Stacking depth for overlapping appointments.
 *
 * Appointment cards are absolutely positioned inside a per-column overlay, so any two
 * cards whose time ranges intersect compete for the same pixels and the later one in
 * DOM order simply paints over the earlier one. When one range *contains* another
 * (09:00–11:00 vs 09:30–10:30) the shorter card is hidden completely.
 *
 * Cards stack one over another rather than shrink side by side, so each gets a depth,
 * and intersecting appointments always land on different depths. The overlay turns
 * depth into both a left offset and a matching z-index: a deeper card is drawn on top
 * of, and indented further than, everything it overlaps, which leaves a strip of the
 * card beneath permanently exposed. See `AppointmentsOverlay` in calendar-view.tsx.
 *
 * Depth is reused as soon as it is free, so a column of appointments that merely follow
 * one another all sit at depth 0 and keep the full width.
 */

export type StackInput = {
  id: string | number;
  /** Minutes from midnight, as actually drawn (i.e. already clamped to the day window). */
  startMin: number;
  /** Minutes from midnight, as actually drawn. */
  endMin: number;
};

export type StackPlacement = {
  /** How many cards this one is stacked over. Drives its left offset and z-index. */
  depth: number;
  /** Distinct depths in this card's collision cluster. Lets the overlay size the offset step. */
  depthCount: number;
};

type Event = StackInput & { order: number };

/**
 * Maps each appointment id to the stacking depth its card should be drawn at.
 * Pure and deterministic: same input list, same output, no DOM measurement.
 */
export function layoutAppointments(items: StackInput[]): Map<StackInput["id"], StackPlacement> {
  const result = new Map<StackInput["id"], StackPlacement>();
  if (items.length === 0) return result;

  // A zero- or negative-length range would collide with nothing and could then be
  // placed at the same depth as a neighbour, so give every event a positive extent.
  const events: Event[] = items.map((it, i) => ({
    id: it.id,
    startMin: it.startMin,
    endMin: Math.max(it.endMin, it.startMin + 1),
    order: i,
  }));

  // Earliest first, longest first on a tie: the longer appointment takes the shallower
  // depth, so the shorter one indents on top of it rather than the reverse. `order` is
  // the final tiebreak so appointments sharing a start *and* an end stay deterministic.
  events.sort((a, b) => a.startMin - b.startMin || b.endMin - a.endMin || a.order - b.order);

  // Split into clusters: maximal runs of events joined transitively by overlap. Depth
  // counts are per-cluster, so an unrelated pileup elsewhere in the day cannot shrink
  // the offset step used here.
  let cluster: Event[] = [];
  let clusterEnd = -Infinity;

  const flush = () => {
    if (cluster.length > 0) assignDepths(cluster, result);
    cluster = [];
    clusterEnd = -Infinity;
  };

  for (const ev of events) {
    if (ev.startMin >= clusterEnd) flush();
    cluster.push(ev);
    clusterEnd = Math.max(clusterEnd, ev.endMin);
  }
  flush();

  return result;
}

function assignDepths(cluster: Event[], result: Map<StackInput["id"], StackPlacement>): void {
  const depthEnds: number[] = []; // end of the last event placed at each depth
  const depthOf: number[] = []; // depth per event, index-aligned with `cluster`

  // Events arrive sorted by start, so a depth whose last event ends at or before this
  // start cannot overlap it — the first such depth is always safe, and taking the
  // shallowest keeps the indent as small as possible.
  for (const ev of cluster) {
    let depth = depthEnds.findIndex((end) => end <= ev.startMin);
    if (depth === -1) {
      depth = depthEnds.length;
      depthEnds.push(ev.endMin);
    } else {
      depthEnds[depth] = ev.endMin;
    }
    depthOf.push(depth);
  }

  const depthCount = depthEnds.length;
  cluster.forEach((ev, i) => result.set(ev.id, { depth: depthOf[i], depthCount }));
}
